/**
 * BarcodeModelSelector - Component for selecting barcode models and generating codes
 * Supports 4 barcode models for AuditLog:
 * - Model A: Two-read barcode (1st GTIN, 2nd Lot/Expiry)
 * - Model B: GS1-128 Single read complete
 * - Model C: DataMatrix Single read complete
 * - Model D: GTIN only (manual lot/expiry)
 */
import React, { useState, useMemo } from 'react';
import { QrCode, Barcode as BarcodeIcon, Copy, ChevronDown, Check, Calendar, Package, Hash } from 'lucide-react';
import Barcode from 'react-barcode';
import { Product } from '../../services/productService';
import { ClickableBarcode } from './ClickableBarcode';
import {
  type BarcodeModel,
  type GeneratedBarcode,
  BARCODE_MODELS,
} from './barcodeModelTypes';

export type { BarcodeModel };

interface BarcodeModelSelectorProps {
  products: Product[];
  onBarcodeGenerated?: (barcode: GeneratedBarcode) => void;
}

export const BarcodeModelSelector: React.FC<BarcodeModelSelectorProps> = ({
  products,
  onBarcodeGenerated,
}) => {
  const [selectedModel, setSelectedModel] = useState<BarcodeModel>('B');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [lote, setLote] = useState('');
  const [validade, setValidade] = useState('');
  const [generatedBarcode, setGeneratedBarcode] = useState<GeneratedBarcode | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch) return products.slice(0, 50); // Limit to 50 initially
    const search = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.nome.toLowerCase().includes(search) ||
        p.codigo.toLowerCase().includes(search) ||
        (p.gtin && p.gtin.includes(search))
    ).slice(0, 50);
  }, [products, productSearch]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId),
    [products, selectedProductId]
  );

  const selectedModelInfo = BARCODE_MODELS.find((m) => m.id === selectedModel);

  // Format date to YYMMDD
  const formatDateToYYMMDD = (dateString: string): string => {
    const date = new Date(dateString);
    const yy = date.getFullYear().toString().slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return yy + mm + dd;
  };

  // Generate barcode based on selected model
  const handleGenerate = () => {
    if (!selectedProduct) return;

    const gtin = selectedProduct.gtin || selectedProduct.ean || '';  // GTIN-S-002: sem GTIN = vazio
    const yymmdd = validade ? formatDateToYYMMDD(validade) : '';

    let generated: GeneratedBarcode;

    switch (selectedModel) {
      case 'A':
        generated = {
          model: 'A',
          product: selectedProduct,
          gtin,
          lote: lote || undefined,
          validade: validade || undefined,
          codigoGtin: `(01)${gtin}`,
          codigoLoteValidade: lote && validade ? `(17)${yymmdd}(10)${lote}` : undefined,
          codigoCompleto: lote && validade ? `(01)${gtin}|(17)${yymmdd}(10)${lote}` : `(01)${gtin}`,
        };
        break;

      case 'B':
        generated = {
          model: 'B',
          product: selectedProduct,
          gtin,
          lote: lote || undefined,
          validade: validade || undefined,
          codigoCompleto: lote && validade ? `(01)${gtin}(17)${yymmdd}(10)${lote}` : `(01)${gtin}`,
        };
        break;

      case 'C':
        generated = {
          model: 'C',
          product: selectedProduct,
          gtin,
          lote: lote || undefined,
          validade: validade || undefined,
          codigoCompleto: lote && validade ? `(01)${gtin}(17)${yymmdd}(10)${lote}` : `(01)${gtin}`,
        };
        break;

      case 'D':
        generated = {
          model: 'D',
          product: selectedProduct,
          gtin,
          codigoCompleto: gtin,
        };
        break;

      default:
        return;
    }

    setGeneratedBarcode(generated);
    onBarcodeGenerated?.(generated);
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const needsLoteValidade = selectedModel !== 'D';

  return (
    <div className="bg-white rounded-xl shadow-lg border border-amber-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <QrCode className="w-6 h-6" />
          Simulador de Código de Barras
        </h2>
        <p className="text-amber-100 text-sm mt-1">
          Gere códigos de barras nos 4 modelos suportados para teste
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Model Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Modelo do Código de Barras
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {BARCODE_MODELS.map((model) => (
              <button
                key={model.id}
                onClick={() => {
                  setSelectedModel(model.id);
                  setGeneratedBarcode(null);
                }}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  selectedModel === model.id
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={selectedModel === model.id ? 'text-amber-600' : 'text-gray-500'}>
                    {model.icon}
                  </div>
                  <span
                    className={`font-semibold text-sm ${
                      selectedModel === model.id ? 'text-amber-700' : 'text-gray-700'
                    }`}
                  >
                    {model.id}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{model.readings === 1 ? '1 leitura' : '2 leituras'}</p>
              </button>
            ))}
          </div>
          {selectedModelInfo && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
              <strong>{selectedModelInfo.name}:</strong> {selectedModelInfo.description}
            </p>
          )}
        </div>

        {/* Product Selection */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Package className="w-4 h-4 inline mr-1" />
            Produto
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowProductDropdown(!showProductDropdown)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between bg-white hover:border-amber-400 transition-colors"
            >
              <span className={selectedProduct ? 'text-gray-900' : 'text-gray-500'}>
                {selectedProduct
                  ? `${selectedProduct.nome} (${selectedProduct.codigo})`
                  : 'Selecione um produto...'}
              </span>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showProductDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                <div className="sticky top-0 p-2 bg-white border-b">
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    autoFocus
                  />
                </div>
                {filteredProducts.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Nenhum produto encontrado
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        setSelectedProductId(product.id);
                        setShowProductDropdown(false);
                        setProductSearch('');
                        setGeneratedBarcode(null);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-amber-50 flex items-center justify-between ${
                        selectedProductId === product.id ? 'bg-amber-100' : ''
                      }`}
                    >
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{product.nome}</div>
                        <div className="text-xs text-gray-500">
                          {product.codigo} • {product.gtin || product.ean || 'Sem GTIN'}
                        </div>
                      </div>
                      {selectedProductId === product.id && <Check className="w-4 h-4 text-amber-600" />}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Lote and Validade (conditional) */}
        {needsLoteValidade && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Hash className="w-4 h-4 inline mr-1" />
                Lote
              </label>
              <input
                type="text"
                value={lote}
                onChange={(e) => {
                  setLote(e.target.value.toUpperCase());
                  setGeneratedBarcode(null);
                }}
                placeholder="Ex: P3G0115"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Validade
              </label>
              <input
                type="date"
                value={validade}
                onChange={(e) => {
                  setValidade(e.target.value);
                  setGeneratedBarcode(null);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={!selectedProduct}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 ${
            selectedProduct
              ? 'bg-amber-500 hover:bg-amber-600'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          <BarcodeIcon className="w-5 h-5" />
          Gerar Código de Barras
        </button>

        {/* Generated Barcode Display */}
        {generatedBarcode && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Código Gerado - Modelo {generatedBarcode.model}
            </h3>

            <div className="space-y-4">
              {/* For Model A - Two barcodes */}
              {generatedBarcode.model === 'A' && (
                <>
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="text-sm text-gray-600 mb-2">1ª Leitura - GTIN:</p>
                    <div className="flex items-center justify-center p-2 bg-white">
                      <ClickableBarcode productName={generatedBarcode.product.nome}>
                        <Barcode
                          value={generatedBarcode.codigoGtin?.replace(/[()]/g, '') || generatedBarcode.gtin}
                          format="CODE128"
                          width={1.5}
                          height={50}
                          displayValue={true}
                          fontSize={12}
                        />
                      </ClickableBarcode>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 bg-gray-100 px-3 py-1 rounded text-sm">
                        {generatedBarcode.codigoGtin}
                      </code>
                      <button
                        onClick={() => copyToClipboard(generatedBarcode.codigoGtin || '', 'gtin')}
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        {copiedField === 'gtin' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {generatedBarcode.codigoLoteValidade && (
                    <div className="bg-white p-4 rounded-lg border">
                      <p className="text-sm text-gray-600 mb-2">2ª Leitura - Lote/Validade:</p>
                      <div className="flex items-center justify-center p-2 bg-white">
                        <ClickableBarcode productName={generatedBarcode.product.nome}>
                          <Barcode
                            value={generatedBarcode.codigoLoteValidade.replace(/[()]/g, '')}
                            format="CODE128"
                            width={1.5}
                            height={50}
                            displayValue={true}
                            fontSize={12}
                          />
                        </ClickableBarcode>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <code className="flex-1 bg-gray-100 px-3 py-1 rounded text-sm">
                          {generatedBarcode.codigoLoteValidade}
                        </code>
                        <button
                          onClick={() => copyToClipboard(generatedBarcode.codigoLoteValidade || '', 'lote')}
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          {copiedField === 'lote' ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* For Models B, C, D - Single barcode */}
              {generatedBarcode.model !== 'A' && (
                <div className="bg-white p-4 rounded-lg border">
                  <p className="text-sm text-gray-600 mb-2">
                    {generatedBarcode.model === 'C' ? 'DataMatrix:' : 'Código:'}
                  </p>

                  {generatedBarcode.model === 'C' ? (
                    // DataMatrix placeholder - will use canvas in DataMatrixGenerator
                    <div className="flex items-center justify-center p-4">
                      <div className="text-center">
                        <QrCode className="w-24 h-24 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">DataMatrix será gerado abaixo</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-2 bg-white">
                      <ClickableBarcode productName={generatedBarcode.product.nome}>
                        <Barcode
                          value={generatedBarcode.codigoCompleto.replace(/[()]/g, '')}
                          format={generatedBarcode.model === 'D' ? 'EAN13' : 'CODE128'}
                          width={generatedBarcode.model === 'D' ? 2 : 1.2}
                          height={50}
                          displayValue={true}
                          fontSize={12}
                        />
                      </ClickableBarcode>
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 px-3 py-1 rounded text-sm overflow-x-auto">
                      {generatedBarcode.codigoCompleto}
                    </code>
                    <button
                      onClick={() => copyToClipboard(generatedBarcode.codigoCompleto, 'completo')}
                      className="p-2 hover:bg-gray-200 rounded flex-shrink-0"
                    >
                      {copiedField === 'completo' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Product Info */}
              <div className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
                <p><strong>Produto:</strong> {generatedBarcode.product.nome}</p>
                <p><strong>GTIN:</strong> {generatedBarcode.gtin}</p>
                {generatedBarcode.lote && <p><strong>Lote:</strong> {generatedBarcode.lote}</p>}
                {generatedBarcode.validade && <p><strong>Validade:</strong> {new Date(generatedBarcode.validade).toLocaleDateString('pt-BR')}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
